import type { ApiMeasure } from "@src/api/dictionaries/measures";
import type { ApiDivision } from "@src/api/dictionaries/divisions";
import type { ApiUserListItem } from "@src/api/users";
import type { StockObj } from "@src/api/stock/stockObjs";
import type { OMPSearchString } from "@src/api/main/OMPTypes";
import type { ApiStockLot } from "../lots";
import type { ApiStorePlaceData, StorePlaceRemainder } from "../storePlaces";

/**
 * Статус комплектования контейнера КАНБАН. \
 * Wire-поле `ComplectState` в `stock/stockKanbanContainer.loadByFilter`.
 */
export const StockKanbanComplectState = {
    /** Не взят на комплектование. */
    Idle: 0,
    /** Взят на комплектование. */
    InProgress: 1,
    /** Комплектование завершено. */
    Finished: 2,
} as const;

/** Значение {@link StockKanbanComplectState}. */
export type StockKanbanComplectStateValue = (typeof StockKanbanComplectState)[keyof typeof StockKanbanComplectState];

/** Фильтр контейнеров КАНБАН — контракт `stock/stockKanbanContainer.loadByFilter`. */
export interface StockKanbanContainerFilter {
    /**
     * Коды подразделений-потребителей (цеха / кладовые). \
     * Wire-поле `DivisionCodes`.
     */
    DivisionCodes?: number[];
    /**
     * Коды участков-потребителей. \
     * Wire-поле `SectionCodes`; пустое значение на форме не передаётся (без ограничения по участку).
     */
    SectionCodes?: number[];
    /**
     * Коды пользователей, взявших контейнер на комплектование. \
     * Используется для чекбокса «только текущий пользователь».
     */
    ComplectUserCodes?: number[];
    /**
     * Статусы комплектования для выборки. \
     * @see {@link StockKanbanComplectState}
     */
    ComplectStates?: StockKanbanComplectStateValue[];
    /** Номер контейнера. */
    Num?: OMPSearchString;
    /**
     * Штрихкод/номер СО контейнера. \
     * Wire-поле `SoNum` (`CLikeSensString` — через `getOmpSearchString`).
     */
    SoNum?: OMPSearchString;
    /** Коды объектов системы. */
    SoCodes?: number[];
    /** Коды ТМЦ. */
    StockobjCodes?: number[];
}

/** Контейнер КАНБАН — контракт `ApiStockKanbanContainerData`. */
export interface ApiStockKanbanContainerData {
    /** Код пользователя-комплектовщика; `0`, если ещё не взят. */
    ComplectUserCode: number;
    /**
     * Дата взятия на комплектование (`YYYY-MM-DD`); пустая строка, если ещё не взят. \
     * Wire-поле `ComplectDate`, только чтение.
     */
    ComplectDate: string;
    /**
     * Статус комплектования. \
     * @see {@link StockKanbanComplectState}
     */
    ComplectState: StockKanbanComplectStateValue;
    /** Код подразделения-потребителя. */
    DivisionCode: number;
    /** Код единицы измерения. */
    MeasureCode: number;
    /** Номер контейнера. */
    Num: string;
    /** Нормативное количество. */
    Qty: number;
    /** Код участка-потребителя. */
    SectionCode: number;
    /** Код объекта системы (идентификатор контейнера в API-методах). */
    SoCode: number;
    /** Код ТМЦ. */
    StockobjCode: number;
}

/** Связи контейнера КАНБАН, догружаемые сервисным слоем. */
export interface StockKanbanContainerExtends {
    /** ТМЦ контейнера. */
    StockObj: StockObj;
    /** Единица измерения нормативного количества. */
    Measure: ApiMeasure;
    /** Подразделение-потребитель. */
    Division: ApiDivision;
    /**
     * Участок-потребитель как производственное подразделение. \
     * Догружается через `storeDivisions.loadByFilter` по `SectionCodes`
     * (`container.SectionCode === division.SectionCode`).
     */
    Section: ApiDivision;
    /**
     * Пользователь-комплектовщик. \
     * Догружается через `user.loadListByFilter` по `ComplectUserCode`.
     */
    ComplectUser: ApiUserListItem;
    /**
     * Ячейки с остатками требуемого ТМЦ. \
     * Догружается через `stock/storePlaces.loadRemaindersByFilter`
     * (`DepotcardType = 1` + `StockobjCodes` контейнера, без `DivisionCode`).
     */
    RemainderCells: StorePlaceRemainder[];
}

/** Контейнер КАНБАН с опционально догруженными связями. */
export type StockKanbanContainer = ApiStockKanbanContainerData & Partial<StockKanbanContainerExtends>;

/** Фильтр партий, выбранных для контейнера КАНБАН. */
export interface StockKanbanContainerContentFilter {
    /** Коды связей партия↔контейнер. */
    Codes?: number[];
    /** Код контейнера (`SoCode`). */
    ContainerCode?: number;
    /** Коды контейнеров для `getLots`. */
    ContainerCodes?: number[];
}

/**
 * Связь партии с контейнером КАНБАН. \
 * Wire: `stock/stockKanbanContainer.getLots` / `addLot`.
 */
export interface ApiStockKanbanContainerContentData {
    /** Код связи партия↔контейнер. */
    Code: number;
    /** Код контейнера (`SoCode`). */
    ContainerCode: number;
    /** Код партии. */
    LotCode: number;
    /** Количество отпуска. */
    Qty: number;
    /**
     * Код места хранения. \
     * `0` / отсутствует, если партия не размещена.
     */
    SpCode: number | 0;
}

/** Связи выбранной партии контейнера. */
export interface StockKanbanContainerContentExtends {
    /** Партия. */
    Lot: ApiStockLot;
    /** Единица измерения контейнера. */
    Measure: ApiMeasure;
    /**
     * Ячейка размещения, из которой скомплектована партия. \
     * Догружается через `stock/storePlaces.loadByFilter` по `SpCode`. \
     * Явный `null` при `SpCode = 0` («на полу») — отличать от `undefined` (include не запрошен).
     */
    StorePlace: ApiStorePlaceData | null;
}

/** Выбранная партия контейнера с опционально догруженными связями. */
export type StockKanbanContainerContent = ApiStockKanbanContainerContentData & Partial<StockKanbanContainerContentExtends>;

/** Payload добавления контейнера в выборку по скану этикетки. */
export interface StockKanbanCreateContainerByScanParams {
    /** Скан этикетки контейнера (`SoNum`). */
    barcode: string;
    /** Текущий фильтр списка (склад и опции). */
    filter: StockKanbanContainerFilter;
}

/** Payload создания выбранной партии контейнера. */
export interface StockKanbanCreateContentParams {
    /** Код контейнера (`SoCode`). */
    containerCode: number;
    /** Код партии. */
    lotCode: number;
    /** Количество отпуска. */
    qty: number;
    /**
     * Код места хранения партии. \
     * Всегда передавать явно: код ячейки или `0`, если партия не размещена («на полу»).
     */
    spCode: number | 0;
}

/** Payload обновления выбранной партии контейнера (через remove + add). */
export interface StockKanbanUpdateContentParams extends StockKanbanCreateContentParams {
    /** Код связи партия↔контейнер. */
    contentCode: number;
}

/** Payload удаления выбранной партии контейнера. */
export interface StockKanbanRemoveContentParams {
    /** Код связи партия↔контейнер. */
    contentCode: number;
}

/** Payload взятия контейнера на комплектование. */
export interface StockKanbanTakeForComplectParams {
    /** Код контейнера (`SoCode`). */
    containerCode: number;
}

/** Payload завершения комплектования одного контейнера. */
export interface StockKanbanCompleteContainerParams {
    /** Код контейнера (`SoCode`) со статусом {@link StockKanbanComplectState.InProgress}. */
    containerCode: number;
}

/** Payload перемещения скомплектованных контейнеров. */
export interface StockKanbanMoveCompletedSelectionParams {
    /**
     * Коды контейнеров (`SoCode`) со статусом {@link StockKanbanComplectState.Finished}. \
     * Обычно — из текущей выборки списка.
     */
    codes: number[];
}

/** Ответ `stock/stockKanbanContainer.moveContainers`. */
export interface StockKanbanMoveContainersResult {
    /** Коды созданных ВЗН СУ. */
    stockInplantCodes: number[];
}
